import { useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Eye, EyeOff, AlertCircle, Check, CheckCircle2 } from 'lucide-react'
import { Button } from '../../../shared/ui/button'
import { Input } from '../../../shared/ui/input'
import { BodyText, HeadingText } from '../../../shared/ui/typography'
import { UserBox } from '../../../shared/assets/svg'
import { cn } from '../../../shared/lib'
import { PAGES } from '../../../shared/lib/pageUrlConstants'
import { SetupAccountDataType } from '../types'
import { useMutation } from '@tanstack/react-query'
import { setupAccountService } from '../service'
import { toast } from 'sonner'
import { InfoCircle } from 'iconsax-reactjs'
import { useGeolocation } from '../../../features/account-settings/hooks/use-geolocation'


const SignIn = () => {
  const navigate = useNavigate()
  const { id, email: emailFromUrl } = useParams<{ id: string, email: string }>() ?? { email: "", id: "" };

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)


  const { userLocation, locationStatus } = useGeolocation();
  const { mutateAsync: setupAccount, isPending: setupAccountLoading } = useMutation({
    mutationFn: (data: SetupAccountDataType) => setupAccountService(data),
    onSuccess: (response) => {
      console.log({ response })
    }
  })

  const passwordRequirements = useMemo(() => {
    return [
      { label: 'Uppercase', met: /[A-Z]/.test(password) },
      { label: 'Lowercase', met: /[a-z]/.test(password) },
      { label: 'Number', met: /[0-9]/.test(password) },
      { label: 'Special character', met: /[^A-Za-z0-9]/.test(password) },
      { label: '8 characters', met: password.length >= 8 },
    ]
  }, [password])

  const passwordsMatch = password === confirmPassword && password !== ''
  const showMatchStatus = password !== '' && confirmPassword !== ''

  const validatePhone = (value: string) => {
    if (!value) {
      setPhoneError('')
      return false
    }

    // Check if it's 11 digits
    if (value.length !== 11) {
      setPhoneError('Phone number must be exactly 11 digits')
      return false
    }

    // Check prefix 070-091
    const prefix = parseInt(value.substring(0, 3))
    if (isNaN(prefix) || prefix < 70 || prefix > 91 || !value.startsWith('0')) {
      setPhoneError('Phone number must start with a valid prefix (070-091)')
      return false
    }

    setPhoneError('')
    return true
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '') // Numbers only
    if (value.length <= 11) {
      setPhone(value)
      if (value.length === 11) {
        validatePhone(value)
      } else {
        setPhoneError('')
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (locationStatus === 'denied') {
      toast.error("Location access is required to complete setup. Please enable location services and reload the page.", { position: "top-center" });
      return;
    }
    // Implementation for setup account
    const data: SetupAccountDataType = {
      email: emailFromUrl!,
      password,
      firstName,
      lastName,
      phoneNumber: phone,
      verificationToken: id!,
      latitude: userLocation?.latitude,
      longitude: userLocation?.longitude
    }
    try {
      const res = await setupAccount(data)
      toast.success(res?.message)
      localStorage.setItem("token", res?.data?.token);
      if (res?.data?.mfaSetupRequired) {
        navigate(PAGES.setup2fa)
      }
    } catch (error: any) {
      toast.error(typeof error?.response?.data?.message !== 'string' ? error?.response?.data?.message?.[0] : error?.response?.data?.message || "Account setup failed")
      console.log({ error })
    }
  }


  return (
    <div className="bg-white md:max-w-[628px] mx-auto w-11/12 rounded-[32px] p-8 md:p-[48px] shadow-[0px_4px_40px_rgba(0,0,0,0.02)] border border-primary-80">
      <div className="flex flex-col items-start gap-6">
        <div className='p-0 relative h-12 mb-2'>
          <UserBox className="absolute -top-5 -left-5" />
        </div>

        <div className="space-y-1">
          <HeadingText className="text-3xl font-semibold leading-tight text-[#161827]">
            Welcome <span className="capitalize">{emailFromUrl!.split("@")[0]},</span>
          </HeadingText>
          <BodyText className="text-[#383B56] text-sm font-[350]">
            Setup a password to access the admin dashboard.
          </BodyText>
        </div>

        <form className="w-full space-y-4 mt-2" onSubmit={handleSubmit}>
          <div className="space-y-1.5">
            <span className="text-[12px] font-[450] text-[#6F739B] px-1">Email</span>
            <Input
              type="email"
              value={emailFromUrl}
              readOnly
              className="h-[56px] px-4 rounded-[8px] bg-primary-80 border-none text-[#5C638E]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="h-[56px] px-4 rounded-[8px] bg-primary-80 border-none"
            />
            <Input
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="h-[56px] px-4 rounded-[8px] bg-primary-80 border-none"
            />
          </div>

          <div className="flex gap-2">
            <div className="flex items-center gap-2 bg-primary-80 rounded-[8px] px-3 h-[56px] min-w-[100px]">
              <span className="text-xl">🇳🇬</span>
              <span className="text-[14px] font-[450] text-[#2C2F44]">+234</span>
            </div>
            <Input
              placeholder="0905 555 0123"
              value={phone}
              onChange={handlePhoneChange}
              onBlur={() => phone.length > 0 && validatePhone(phone)}
              className={cn(
                "h-[56px] px-4 rounded-[8px] bg-primary-80 border-none flex-1",
                phoneError && "ring-1 ring-error-30"
              )}
            />
          </div>
          {phoneError && <p className="text-error-30 text-[12px] font-[350] px-1">{phoneError}</p>}

          <div className="space-y-1.5">
            <span className="text-[12px] font-[450] text-[#6F739B] px-1">New Password</span>
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-[56px] px-4 rounded-[8px] bg-primary-80 border-none"
              endIcon={
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-[#6F739B]">
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              }
            />
          </div>

          <div className="flex flex-wrap gap-2 py-1">
            {passwordRequirements.map((req, index) => (
              <div
                key={index}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-[450] transition-colors",
                  req.met
                    ? "bg-[#DCFDDB] text-[#188B18] border border-[#1DCB1C]"
                    : "bg-primary-80 text-[#6F739B]"
                )}
              >
                {req.met && <Check size={14} />}
                {req.label}
              </div>
            ))}
          </div>

          <div className="space-y-1.5">
            <span className="text-[12px] font-[450] text-[#6F739B] px-1">Confirm Password</span>
            <Input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="h-[56px] px-4 rounded-[8px] bg-primary-80 border-none"
              endIcon={
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="text-[#6F739B]">
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              }
            />
          </div>

          <div className="space-y-2 mt-2">
            {(showMatchStatus && !passwordsMatch) && (
              <div className={cn("flex items-center gap-2 text-[12px] font-[450]", passwordsMatch ? "text-[#6F739B]" : "text-[#E24B4B]")}>
                {passwordsMatch ? <InfoCircle size={16} className="text-[#008B52]" /> : <AlertCircle size={16} className="text-[#EAAC08]" />}
                <span>Passwords must match</span>
              </div>
            )}
            {(passwordsMatch && showMatchStatus) && (
              <div className="flex items-center gap-2 text-[12px] font-[450] text-[#008B52]">
                <CheckCircle2 className="text-[#008B52]" size={16} />
                <span>Passwords match</span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-6 pt-6">
            <a href="mailto:admin@tradevu.com" target="_blank" rel="noopener noreferrer" className="text-[14px] font-[450] text-[#161827]">Having trouble?</a>
            <Button
              type="submit"
              size="lg"
              disabled={setupAccountLoading}
              className="!h-[48px] px-8 rounded-xl bg-[#161827] text-white hover:bg-[#20243a]"
            >
              {setupAccountLoading ? "Loading..." : "Setup account"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SignIn
